import {
	Icon,
	Modal,
	NewClientDisclaimerModal,
	WelcomeModal,
	Wizard,
} from "@app/components";
import {
	type ValidationErrors,
	commonMessages,
	countryList,
	countrySouthAfrica,
	isRegistrationLocked,
	provinceList,
	provinceOther,
	validationErrorOptions,
} from "@app/constants";
import { Button, Checkbox, Dropdown, Input } from "@app/controls";
import type { DropdownItem } from "@app/entities";
import type { RootState } from "@app/redux";
import { Transition } from "@headlessui/react";
import { Formik, type FormikErrors } from "formik";
import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { RegistrationPauseModal } from "../registration-pause-modal";
import messages from "./messages";
import type {
	AddressFieldKey,
	AddressFieldOptions,
} from "./models/address-fields";
import type { DropdownFieldKey } from "./models/dropdown-fields";
import type { FormValues } from "./models/form";
import "./new-client-modal.css";
import "./new-client-modal.responsive.css";
import { FaChevronLeft } from "react-icons/fa";
import type { ViewProperties } from "./properties";

export const NewClientModalView = React.memo((props: ViewProperties) => {
	const { language } = useSelector((state: RootState) => state.language);

	const errorOuterStyle = ["flex", "flex-col", "gap-y-2"].join(" ");

	const errorInnerStyle = ["font-primary-regular", "new-client-error"].join(
		" ",
	);

	const inputRowStyle = ["grow", "mt-auto"].join(" ");

	// #region CONSTANTS
	const countryItems = React.useMemo(() => {
		return countryList.map((x) => ({
			value: x,
		}));
	}, []);

	const residentialProvinceItems = React.useMemo(() => {
		const list =
			props.residentialCountry === countrySouthAfrica
				? provinceList.filter((x) => x !== provinceOther)
				: provinceList;

		return list.map((x) => ({
			label: x === "Kwazulu-Natal" ? "KwaZulu-Natal" : x,
			value: x,
		}));
	}, [props.residentialCountry]);

	const postalProvinceItems = React.useMemo(() => {
		const list =
			props.postalCountry === countrySouthAfrica
				? provinceList.filter((x) => x !== provinceOther)
				: provinceList;

		return list.map((x) => ({
			label: x === "Kwazulu-Natal" ? "KwaZulu-Natal" : x,
			value: x,
		}));
	}, [props.postalCountry]);

	const referrerOptions = React.useMemo(() => {
		return props.referrerOptions.map<DropdownItem<string>>((x) => ({
			value: String(x.id),
			label: x.name,
		}));
	}, [props.referrerOptions]);

	const postalResidentialAddressItems = useMemo(() => {
		const labels = messages[language].postalResidentialAddressItems;

		return [
			{ label: labels.no, value: "no" },
			{ label: labels.yes, value: "yes" },
		];
	}, [language]);

	const getHtmlFromText = (text: string) => (
		<div dangerouslySetInnerHTML={{ __html: text }} />
	);

	const getError = (show: boolean) => {
		const areErrors =
			show ||
			(props.mappedErrors !== undefined &&
				Object.entries(props.mappedErrors).find((x) => x[1] !== undefined) !==
					undefined);

		return (
			<div className="error pb-5 flex justify-center">
				<span>
					<Transition
						show={areErrors}
						enter="transition ease-out duration-300"
						enterFrom="opacity-0 translate-y-5"
						leave="transition ease-out duration-300"
						leaveTo="opacity-0 -translate-y-5"
					>
						<p>{messages[language].errors.generic}</p>
					</Transition>
				</span>
			</div>
		);
	};

	const getFieldErrorContent = (fieldName: string, localError?: string) => {
		const errors = getFieldErrors(fieldName, localError);
		const showCondition = isFieldInError(fieldName, localError);

		return (
			<Transition
				show={showCondition}
				enter="transition ease-out duration-300"
				leave="transition east-out duration-300"
				enterFrom="opacity-0 translate-y-5"
				enterTo="opacity-100 translate-y-0"
				leaveTo="opacity-0 -translate-y-5"
			>
				{errors && (
					<div className={errorOuterStyle}>
						<div
							className={errorInnerStyle}
							dangerouslySetInnerHTML={{
								__html: `<p>${errors.join("</p><p>")}</p>`,
							}}
						/>
					</div>
				)}
			</Transition>
		);
	};

	const getHeader = () => {
		return (
			<div className="main-heading mb-10">
				<h2 className="mb-5">
					{props.clients.length === 0
						? messages[language].headerInitial
						: messages[language].header}
				</h2>
				<div className="heading-underline" />
			</div>
		);
	};

	const isFieldInError = (
		key: string,
		localError?: string,
	): boolean | undefined => {
		if (localError) {
			return true;
		}

		let result = false;

		if (props.mappedErrors) result = (props.mappedErrors[key] ?? []).length > 0;

		if (!result) return undefined;

		return result;
	};

	const getFieldErrors = (
		key: string,
		localError?: string,
	): string[] | undefined => {
		if (localError) {
			return [mapControlError(localError)];
		}

		let result: string[] = [];

		if (props.mappedErrors) {
			result = props.mappedErrors[key] ?? [];
		}

		if (result.length > 0) {
			return result;
		}

		return undefined;
	};

	const mapControlError = (error: string) => {
		const validationError = error as ValidationErrors;

		if (validationErrorOptions.includes(validationError)) {
			if (validationError && validationError.length > 0) {
				return commonMessages[language].validationError[validationError];
			}
			return commonMessages[language].validationError.required;
		}
		return commonMessages[language].validationError.required;
	};

	const getStep1 = () => {
		const defaultValues: FormValues = {
			...{
				firstName: "",
				middleName: "",
				lastName: "",
				contactNumber: "",
				initialInvestment: "",
				secondaryEmail: "",
				ageAndResidency: undefined,
				sourceOfFunds: undefined,
			},

			...props.defaultValues,
		};

		return (
			<Formik initialValues={defaultValues} onSubmit={props.onSubmit}>
				{({ handleSubmit, validateForm, errors }) => {
					return (
						<>
							{getHeader()}
							<div className="flex flex-wrap flex-1 gap-y-4.5 mb-9.5">
								<div className="grid sm:grid-cols-2 w-full gap-x-11 gap-y-4.5">
									<div className="flex flex-col w-full gap-y-4.5">
										<div className={inputRowStyle}>
											{getFieldErrorContent("firstName", errors.firstName)}
											<Input
												className="w-full lg:flex-1"
												defaultValue={
													defaultValues.firstName as string | undefined
												}
												error={isFieldInError("firstName")}
												maxLength={30}
												name="firstName"
												placeholder={messages[language].firstName}
												required
												title={messages[language].tooltip.firstName}
												useFormik
												validateOnChange={props.validate}
												onChange={() => props.onClearErrors("firstName")}
											/>
										</div>
										<div className={inputRowStyle}>
											{getFieldErrorContent("middleName", errors.middleName)}
											<Input
												className="w-full lg:flex-1"
												defaultValue={
													defaultValues.middleName as string | undefined
												}
												error={isFieldInError("middleName")}
												maxLength={30}
												name="middleName"
												placeholder={messages[language].middleName}
												title={messages[language].tooltip.middleName}
												useFormik
												onChange={() => props.onClearErrors("middleName")}
											/>
										</div>
										<div className={inputRowStyle}>
											{getFieldErrorContent("lastName", errors.lastName)}
											<Input
												className="w-full lg:flex-1"
												defaultValue={
													defaultValues.lastName as string | undefined
												}
												error={isFieldInError("lastName")}
												maxLength={30}
												name="lastName"
												placeholder={messages[language].lastName}
												required
												title={messages[language].tooltip.lastName}
												useFormik
												validateOnChange={props.validate}
												onChange={() => props.onClearErrors("lastName")}
											/>
										</div>
									</div>
									<div className="flex flex-col w-full gap-y-4.5">
										<div className={inputRowStyle}>
											{getFieldErrorContent(
												"initialInvestment",
												errors.initialInvestment,
											)}
											<Input
												className="w-full lg:flex-1"
												defaultValue={
													defaultValues.initialInvestment as string | undefined
												}
												error={isFieldInError("initialInvestment")}
												maxLength={30}
												name="initialInvestment"
												placeholder={messages[language].initialInvestment}
												required
												title={messages[language].tooltip.initialInvestment}
												type="currency"
												scrollDisabled={true}
												useFormik
												validateOnChange={props.validate}
												onChange={() =>
													props.onClearErrors("initialInvestment")
												}
											/>
										</div>
										<div className={inputRowStyle}>
											{getFieldErrorContent(
												"secondaryEmail",
												errors.secondaryEmail,
											)}
											<Input
												className="w-full lg:flex-1"
												defaultValue={
													defaultValues.secondaryEmail as string | undefined
												}
												error={isFieldInError("secondaryEmail")}
												maxLength={254}
												name="secondaryEmail"
												placeholder={
													props.clients.length > 0
														? messages[language].email
														: messages[language].secondaryEmail
												}
												title={messages[language].tooltip.secondaryEmail}
												type="email"
												useFormik
												validateOnChange={props.validate}
												onChange={() => props.onClearErrors("secondaryEmail")}
											/>
										</div>

										<div className={inputRowStyle}>
											{getFieldErrorContent(
												"contactNumber",
												errors.contactNumber,
											)}
											<Input
												className="w-full lg:flex-1"
												defaultValue={
													defaultValues.contactNumber as string | undefined
												}
												error={isFieldInError("contactNumber")}
												maxLength={20}
												name="contactNumber"
												placeholder={messages[language].contactNumber}
												required
												title={messages[language].tooltip.contactNumber}
												type="tel"
												useFormik
												validateOnChange={props.validate}
												onChange={() => props.onClearErrors("contactNumber")}
											/>
										</div>
									</div>
								</div>
								<div className="flex flex-col w-full gap-y-4.5">
									<h4 className="secondary-heading">
										{messages[language].ageAndResidencyHeader}
									</h4>
									{getFieldErrorContent(
										"ageAndResidency",
										errors.ageAndResidency,
									)}
									<Checkbox
										name="ageAndResidency"
										label={messages[language].ageAndResidencyLabel}
										onClick={() => props.onClearErrors("ageAndResidency")}
										required
										useFormik
										validateOnChange={props.validate}
										defaultValue={
											defaultValues.ageAndResidency as boolean | undefined
										}
									/>
									<h4 className="secondary-heading">
										{messages[language].sourceOfFundsHeader}
									</h4>
									{getFieldErrorContent("sourceOfFunds", errors.sourceOfFunds)}
									<Checkbox
										name="sourceOfFunds"
										label={getHtmlFromText(
											messages[language].sourceOfFundsLabel,
										)}
										onClick={() => props.onClearErrors("sourceOfFunds")}
										required
										useFormik
										validateOnChange={props.validate}
										defaultValue={
											defaultValues.sourceOfFunds as boolean | undefined
										}
									/>
								</div>
							</div>
							{getError(Object.keys(errors).length > 0)}
							<div className="flex justify-center mx-auto">
								<Button
									className="mx-auto"
									type="button"
									theme="primary"
									onClick={async () =>
										props.onValidate(await validateForm(), handleSubmit)
									}
								>
									{messages[language].nextStep}
								</Button>
							</div>
						</>
					);
				}}
			</Formik>
		);
	};

	const getOtherProvinceFields = (
		name: string,
		defaultValue?: string,
		tooltip?: string,
		error?: string,
	) => {
		return (
			<div className="w-full">
				{getFieldErrorContent(name, error)}
				<Input
					className="w-full"
					defaultValue={defaultValue}
					error={isFieldInError(name)}
					name={name}
					placeholder={messages[language].otherProvince}
					required
					title={tooltip}
					useFormik
					validateOnChange={props.validate}
					onChange={() => props.onClearErrors(name)}
				/>
			</div>
		);
	};

	const getAddressFields = ({
		inputClasses,
		fieldOptions,
	}: {
		inputClasses: string;
		fieldOptions: AddressFieldOptions;
	}) => {
		const getName = (name: AddressFieldKey) => {
			return fieldOptions[name].name;
		};
		const getDefaultValue = (name: AddressFieldKey) => {
			return fieldOptions[name].defaultValue;
		};

		const getAddressError = (name: AddressFieldKey) => {
			return fieldOptions[name].error;
		};

		const getTooltip = (name: AddressFieldKey) => {
			return fieldOptions[name].tooltip;
		};

		const province = getName("province");

		let provinceItems = residentialProvinceItems;

		let showOtherProvince = props.showOtherProvinceFields;

		let forceOtherProvince =
			props.residentialCountry &&
			props.residentialCountry !== countrySouthAfrica;

		let selectedProvince = props.residentialProvince;

		if (province === ("postalProvince" as DropdownFieldKey)) {
			provinceItems = postalProvinceItems;

			showOtherProvince = props.showOtherPostalProvinceFields;

			forceOtherProvince =
				props.postalCountry && props.postalCountry !== countrySouthAfrica;

			selectedProvince = props.postalProvince;
		}

		return (
			<div className="w-full">
				<div className="gap-x-11 gap-y-4.5 grid sm:grid-cols-2 mb-4.5">
					<div className={inputRowStyle}>
						{getFieldErrorContent(
							getName("address"),
							getAddressError("address"),
						)}
						<Input
							className={inputClasses}
							defaultValue={getDefaultValue("address")}
							error={isFieldInError(getName("address"))}
							maxLength={100}
							name={getName("address")}
							placeholder={messages[language].streetAddress}
							required
							title={getTooltip("address")}
							useFormik
							validateOnChange={props.validate}
							onChange={() => props.onClearErrors(getName("address"))}
						/>
					</div>
					<div className={inputRowStyle}>
						{getFieldErrorContent(getName("suburb"), getAddressError("suburb"))}
						<Input
							className={inputClasses}
							defaultValue={getDefaultValue("suburb")}
							error={isFieldInError(getName("suburb"))}
							maxLength={50}
							name={getName("suburb")}
							placeholder={messages[language].suburb}
							required
							title={getTooltip("suburb")}
							useFormik
							validateOnChange={props.validate}
							onChange={() => props.onClearErrors(getName("suburb"))}
						/>
					</div>
				</div>
				<div className="gap-x-11 gap-y-4.5 grid sm:grid-cols-2 mb-4.5">
					<div className={inputRowStyle}>
						{getFieldErrorContent(getName("city"), getAddressError("city"))}
						<Input
							className={[inputClasses, "pr-0"].join(" ")}
							defaultValue={getDefaultValue("city")}
							error={isFieldInError(getName("city"))}
							maxLength={30}
							name={getName("city")}
							placeholder={messages[language].city}
							required
							title={getTooltip("city")}
							useFormik
							validateOnChange={props.validate}
							onChange={() => props.onClearErrors(getName("city"))}
						/>
					</div>
					<div className={inputRowStyle}>
						{getFieldErrorContent(
							getName("country"),
							getAddressError("country"),
						)}
						<Dropdown
							className={inputClasses}
							header={messages[language].country}
							items={countryItems}
							name={getName("country")}
							onSelect={(value) =>
								props.onDropdownSelect(
									getName("country") as DropdownFieldKey,
									value,
								)
							}
							required
							theme="secondary"
							useFormik
							validateOnChange={props.validate}
							defaultValue={getDefaultValue("country")}
						/>
					</div>
				</div>
				<div
					className={`gap-x-11 gap-y-4.5 grid sm:grid-cols-2 ${
						showOtherProvince ? "mb-4.5" : ""
					}`}
				>
					<div className={inputRowStyle}>
						{getFieldErrorContent(
							getName("postalCode"),
							getAddressError("postalCode"),
						)}
						<Input
							className={[inputClasses, "pr-0"].join(" ")}
							defaultValue={getDefaultValue("postalCode")}
							error={isFieldInError(getName("postalCode"))}
							maxLength={10}
							name={getName("postalCode")}
							placeholder={messages[language].postalCode}
							required
							title={getTooltip("postalCode")}
							useFormik
							validateOnChange={props.validate}
							onChange={() => props.onClearErrors(getName("postalCode"))}
						/>
					</div>
					<div className={inputRowStyle}>
						{getFieldErrorContent(
							getName("province"),
							getAddressError("province"),
						)}
						<Dropdown
							className={inputClasses}
							header={messages[language].province}
							items={provinceItems}
							disabled={forceOtherProvince}
							name={province}
							selectedValue={
								forceOtherProvince ? provinceOther : selectedProvince
							}
							required
							theme="secondary"
							useFormik
							validateOnChange={props.validate}
							onSelect={(value) =>
								props.onDropdownSelect(
									getName("province") as DropdownFieldKey,
									value,
								)
							}
							defaultValue={getDefaultValue("province")}
						/>
					</div>
				</div>
				{showOtherProvince &&
					getOtherProvinceFields(
						getName("otherProvince"),
						getDefaultValue("otherProvince"),
						getTooltip("otherProvince"),
						getAddressError("otherProvince"),
					)}
			</div>
		);
	};

	const getPostalAddressFields = ({
		inputClasses,
		errors,
	}: {
		inputClasses: string;
		errors: FormikErrors<FormValues>;
	}) => {
		return (
			<>
				<h4 className="secondary-heading w-full">
					{messages[language].postalAddressHeader}
				</h4>
				{getAddressFields({
					inputClasses,
					fieldOptions: {
						address: {
							name: "postalAddress",
							defaultValue: props.defaultValues.postalAddress as
								| string
								| undefined,
							tooltip: messages[language].tooltip.postal.address,
							error: errors.postalAddress,
						},
						suburb: {
							name: "postalSuburb",
							defaultValue: props.defaultValues.postalSuburb as
								| string
								| undefined,
							tooltip: messages[language].tooltip.postal.suburb,
							error: errors.postalSuburb,
						},
						city: {
							name: "postalCity",
							defaultValue: props.defaultValues.postalCity as
								| string
								| undefined,
							tooltip: messages[language].tooltip.postal.city,
							error: errors.postalCity,
						},
						province: {
							name: "postalProvince",
							defaultValue: props.defaultValues.postalProvince as
								| string
								| undefined,
							tooltip: messages[language].tooltip.postal.province,
							error: errors.postalProvince,
						},
						otherProvince: {
							name: "postalProvinceOther",
							defaultValue: props.defaultValues.postalProvinceOther as
								| string
								| undefined,
							tooltip: messages[language].tooltip.postal.provinceOther,
							error: errors.postalProvinceOther,
						},
						postalCode: {
							name: "postalPostalCode",
							defaultValue: props.defaultValues.postalPostalCode as
								| string
								| undefined,
							tooltip: messages[language].tooltip.postal.postalCode,
							error: errors.postalPostalCode,
						},
						country: {
							name: "postalCountry",
							defaultValue: props.defaultValues.postalCountry as
								| string
								| undefined,
							tooltip: messages[language].tooltip.postal.country,
							error: errors.postalCountry,
						},
					},
				})}
			</>
		);
	};

	const getReferredByDetailsFields = (defaultValue: string | undefined) => {
		return (
			<Input
				className="w-full lg:w-auto flex-1 pr-0 force-grow"
				defaultValue={defaultValue}
				error={isFieldInError("referredByDetails")}
				name="referredByDetails"
				placeholder={messages[language].referredByDetails}
				required
				title={messages[language].tooltip.referredByDetails}
				useFormik
				validateOnChange={props.validate}
				onChange={() => props.onClearErrors("referredByDetails")}
			/>
		);
	};

	const getStep2 = () => {
		const defaultValues: FormValues = {
			...{
				idNumber: "",
				taxNumber: "",
				residentialAddress: "",
				residentialSuburb: "",
				residentialCity: "",
				residentialProvince: "",
				residentialProvinceOther: "",
				residentialPostalCode: "",
				residentialCountry: "",
				postalAddress: "",
				postalSuburb: "",
				postalCity: "",
				postalProvince: "",
				postalProvinceOther: "",
				postalPostalCode: "",
				postalCountry: "",
				isPostalSameAsResidentialAddress: undefined,
				referredBy: undefined,
				referredByDetails: undefined,
				acceptedTerms: undefined,
			},
			...props.defaultValues,
		};

		// #region CONSTANTS
		const inputClasses = "w-full lg:flex-1";

		return (
			<Formik initialValues={defaultValues} onSubmit={props.onSubmit}>
				{({ handleSubmit, validateForm, errors, values }) => {
					return (
						<>
							{getHeader()}
							<div className="grid sm:grid-cols-2 gap-y-4.5 gap-x-11 mb-4.5">
								<div className={inputRowStyle}>
									{getFieldErrorContent("idNumber", errors.idNumber)}
									<Input
										className={inputClasses}
										defaultValue={defaultValues.idNumber as string | undefined}
										error={isFieldInError("idNumber")}
										maxLength={13}
										name="idNumber"
										placeholder={messages[language].idNumber}
										required
										title={messages[language].tooltip.idNumber}
										useFormik
										validateOnChange={props.validate}
										onChange={() => props.onClearErrors("idNumber")}
									/>
								</div>
								<div className={inputRowStyle}>
									{getFieldErrorContent("taxNumber", errors.taxNumber)}
									<Input
										className={inputClasses}
										defaultValue={defaultValues.taxNumber as string | undefined}
										error={isFieldInError("taxNumber")}
										maxLength={10}
										name="taxNumber"
										placeholder={messages[language].taxNumber}
										required
										title={messages[language].tooltip.taxNumber}
										useFormik
										validateOnChange={props.validate}
										onChange={() => props.onClearErrors("taxNumber")}
									/>
								</div>
							</div>
							<div className="flex flex-1 flex-wrap gap-y-4.5 gap-x-11 mb-9.5">
								<h4 className="secondary-heading w-full">
									{messages[language].residentialAddressHeader}
								</h4>
								{getAddressFields({
									inputClasses,
									fieldOptions: {
										address: {
											name: "residentialAddress",
											defaultValue: defaultValues.residentialAddress as
												| string
												| undefined,
											tooltip: messages[language].tooltip.residential.address,
											error: errors.residentialAddress,
										},
										suburb: {
											name: "residentialSuburb",
											defaultValue: defaultValues.residentialSuburb as
												| string
												| undefined,
											tooltip: messages[language].tooltip.residential.suburb,
											error: errors.residentialSuburb,
										},
										city: {
											name: "residentialCity",
											defaultValue: defaultValues.residentialCity as
												| string
												| undefined,
											tooltip: messages[language].tooltip.residential.city,
											error: errors.residentialCity,
										},
										province: {
											name: "residentialProvince",
											defaultValue: defaultValues.residentialProvince as
												| string
												| undefined,
											tooltip: messages[language].tooltip.residential.province,
											error: errors.residentialProvince,
										},
										otherProvince: {
											name: "residentialProvinceOther",
											defaultValue: defaultValues.residentialProvinceOther as
												| string
												| undefined,
											tooltip:
												messages[language].tooltip.residential.provinceOther,
											error: errors.residentialProvinceOther,
										},
										postalCode: {
											name: "residentialPostalCode",
											defaultValue: defaultValues.residentialPostalCode as
												| string
												| undefined,
											tooltip:
												messages[language].tooltip.residential.postalCode,
											error: errors.residentialPostalCode,
										},
										country: {
											name: "residentialCountry",
											defaultValue: defaultValues.residentialCountry as
												| string
												| undefined,
											tooltip: messages[language].tooltip.residential.country,
											error: errors.residentialCountry,
										},
									},
								})}
								<div className="grow mt-auto w-full">
									{getFieldErrorContent(
										"isPostalSameAsResidentialAddress",
										errors.isPostalSameAsResidentialAddress,
									)}
									<Dropdown
										className="w-full"
										theme="secondary"
										name="isPostalSameAsResidentialAddress"
										header={messages[language].postalResidentialAddress}
										items={postalResidentialAddressItems}
										onSelect={(value) =>
											props.onDropdownSelect(
												"isPostalSameAsResidentialAddress",
												value,
											)
										}
										required
										useFormik
										validateOnChange={props.validate}
										defaultValue={
											defaultValues.isPostalSameAsResidentialAddress as
												| string
												| undefined
										}
									/>
								</div>
								{props.showPostalAddressFields &&
									getPostalAddressFields({
										inputClasses,
										errors,
									})}
								{props.clients.length === 0 && (
									<div className="w-full">
										<div className="gap-x-11 gap-y-4.5 grid sm:grid-cols-2 mb-4.5">
											<div className={["grow mt-auto"].join(" ")}>
												{getFieldErrorContent("referredBy", errors.referredBy)}
												<Dropdown
													className="min-h-0 w-full lg:flex-1"
													theme="secondary"
													name="referredBy"
													header={messages[language].heardAboutUs}
													items={referrerOptions}
													onSelect={(value) =>
														props.onDropdownSelect("referredBy", value)
													}
													required
													useFormik
													defaultValue={
														defaultValues.referredBy as string | undefined
													}
												/>
											</div>
											{props.showHeardAboutUsFields && (
												<div className="grow mt-auto">
													{getFieldErrorContent(
														"referredByDetails",
														errors.referredByDetails,
													)}
													{getReferredByDetailsFields(
														defaultValues.referredByDetails as
															| string
															| undefined,
													)}
												</div>
											)}
										</div>
									</div>
								)}
								<div
									className={[
										"terms-and-conditions flex flex-col gap-y-[15px] w-full",
									].join(" ")}
								>
									<h4 className="secondary-heading">
										{messages[language].termsAndConditionsHeader}
									</h4>
									<div className={inputRowStyle}>
										{getFieldErrorContent(
											"acceptedTerms",
											errors.acceptedTerms,
										)}
										<Checkbox
											name="acceptedTerms"
											label={getHtmlFromText(
												messages[language].termsAndConditions,
											)}
											onClick={() => props.onClearErrors("acceptedTerms")}
											required
											useFormik
											validateOnChange={props.validate}
											defaultValue={
												defaultValues.acceptedTerms as boolean | undefined
											}
										/>
									</div>
								</div>
							</div>
							{getError(Object.keys(errors).length > 0)}
							<div className="flex justify-center mx-auto">
								<Button
									className="w-full font-primary-medium text-lg"
									type="button"
									theme="tertiary"
									onClick={() => {
										props.onBack(values);
									}}
								>
									<FaChevronLeft className="mr-1" size="14px" />
									{messages[language].back}
								</Button>
								<Button
									className="w-full mx-1"
									type="button"
									theme="primary"
									onClick={async () =>
										props.onValidate(await validateForm(), handleSubmit)
									}
								>
									{messages[language].submit}
								</Button>
							</div>
						</>
					);
				}}
			</Formik>
		);
	};

	const getForms = () => {
		return [getStep1(), getStep2()];
	};

	const getFormCompleted = () => {
		return (
			<h1 className="success font-primary-bold mt-3">
				{messages[language].newClientSuccess}
			</h1>
		);
	};

	const showWelcomeModal =
		props.showWelcome &&
		props.clients.length === 0 &&
		!props.firstClientDisclaimed &&
		props.open;

	const showNewClientDisclaimerModal =
		props.clients.length === 1 &&
		!props.firstClientDisclaimed &&
		props.open &&
		props.showDisclaimer;

	const showWizard = !props.showWelcome && props.open && props.showWizard;

	if (isRegistrationLocked)
		return (
			<RegistrationPauseModal
				open
				onClose={props.onClose}
				logoutOnClose={showWelcomeModal || showNewClientDisclaimerModal}
			/>
		);

	return (
		<>
			{showWelcomeModal && (
				<WelcomeModal open onProceed={props.onDisclaimerProceed} />
			)}
			{showNewClientDisclaimerModal && (
				<NewClientDisclaimerModal
					open
					onClose={props.onClose}
					onCloseTransitionEnd={props.onShowWizard}
					onProceed={props.onDisclaimerProceed}
				/>
			)}
			{showWizard && (
				<Modal
					className="new-client-modal w-full h-full flex flex-col items-center"
					overlayColor="light"
					open
					onClose={props.onClose}
					onCloseTransitionEnd={props.onResetWizard}
				>
					<div className="new-client-container flex flex-col">
						<Wizard
							step={props.step}
							forms={getForms()}
							isComplete={props.step >= props.totalSteps}
							completeForm={getFormCompleted()}
						/>
					</div>
				</Modal>
			)}
		</>
	);
});
